import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";
import { ShieldCheck, ArrowRight, Mail, HelpCircle, Monitor, Smartphone } from "lucide-react";
import { markJustRegistered } from "@/components/AddToHomeScreen";

const SUPPORT_EMAIL = "support@mail.mytabletopapp.co.uk";
const MOBILE_APP_URL = process.env.REACT_APP_MOBILE_APP_URL || "https://kidpreneur-app-1.emergent.host";
const VIEW_STORAGE_KEY = "mttApp:viewPreference";

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "register" ? "register" : "login";
  const [form, setForm] = useState({ email: "", password: "", name: "", mobile_number: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotEmailOpen, setForgotEmailOpen] = useState(false);
  const [viewPref, setViewPref] = useState(() => {
    try { return localStorage.getItem(VIEW_STORAGE_KEY) === "mobile" ? "mobile" : "desktop"; }
    catch { return "desktop"; }
  });
  const [justAuthed, setJustAuthed] = useState(false);

  // Only auto-redirect existing sessions when the user hasn't just authed
  // (so the viewPref choice above governs freshly-authed redirects).
  useEffect(() => {
    if (user && user !== false && !justAuthed) navigate("/dashboard/parent");
  }, [user, navigate, justAuthed]);

  const persistViewPref = (v) => {
    setViewPref(v);
    try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch (_) { /* localStorage unavailable */ }
  };

  const switchMode = (m) => { const p = new URLSearchParams(params); p.set("mode", m); setParams(p); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    // Suppress the "user became truthy → auto-redirect" effect from firing on
    // the partial /auth/login response user (which lacks wallet_* fields).
    // Set the flag BEFORE calling login so the effect stays quiet across the
    // full login + /auth/me refresh sequence. Otherwise the dashboard would
    // mount with a shallow user and first-paint £0.00.
    setJustAuthed(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        await register({
          email: form.email, password: form.password,
          name: form.name, mobile_number: form.mobile_number,
        });
        // Post-registration hook — ParentDashboard picks up on this flag and
        // opens the Add-to-Home-Screen modal (mobile only).
        markJustRegistered();
      }
      if (viewPref === "mobile") {
        // Open the mobile app in a new tab; keep the current tab on the web
        // dashboard so users are never stranded if the mobile URL 404s.
        window.open(MOBILE_APP_URL, "_blank", "noopener,noreferrer");
        navigate("/dashboard/parent");
        return;
      }
      navigate("/dashboard/parent");
    } catch (e) {
      // Failed login/register — reset the guard so the user can try again.
      setJustAuthed(false);
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="pill bg-blue-100 text-blue-800">{mode === "register" ? "Create your account" : "Welcome back"}</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3">
            {mode === "register" ? "Join MyTableTop" : "Sign in"}
          </h1>
          <p className="text-slate-600 mt-3 max-w-md">
            One account per <strong>parent</strong>. You can add multiple kids inside your account once you&rsquo;re in.
          </p>
          <div className="mt-8 card-base p-5 max-w-sm">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Safe for kids</div>
            <p className="text-sm text-slate-600 mt-2">Every listing is checked by AI moderation. Parents approve sensitive actions with a PIN.</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="card-base p-8 self-start" data-testid="auth-form">
          {/* Desktop / Mobile view preference — governs post-auth redirect */}
          <div className="mb-5" data-testid="view-preference-picker">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
              After sign-in, take me to
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => persistViewPref("desktop")}
                className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition-all ${
                  viewPref === "desktop"
                    ? "border-brandPink bg-brandPink/10 text-brandPink shadow-soft"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
                data-testid="view-pref-desktop"
                aria-pressed={viewPref === "desktop"}
              >
                <Monitor className="w-4 h-4" /> Desktop View
              </button>
              <button
                type="button"
                onClick={() => persistViewPref("mobile")}
                className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition-all ${
                  viewPref === "mobile"
                    ? "border-brandTeal bg-brandTeal/10 text-brandTeal shadow-soft"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
                data-testid="view-pref-mobile"
                aria-pressed={viewPref === "mobile"}
              >
                <Smartphone className="w-4 h-4" /> Mobile View
              </button>
            </div>
            {viewPref === "mobile" && (
              <div className="text-[11px] text-slate-500 mt-2" data-testid="view-pref-mobile-hint">
                The mobile app will open in a new tab after sign-in.
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-6">
            <button type="button" onClick={() => switchMode("login")}
              className={`flex-1 py-2 rounded-full font-bold text-sm ${mode === "login" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
              data-testid="tab-login">Sign in</button>
            <button type="button" onClick={() => switchMode("register")}
              className={`flex-1 py-2 rounded-full font-bold text-sm ${mode === "register" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
              data-testid="tab-register">Create account</button>
          </div>

          {mode === "register" && (
            <>
              <label className="block text-sm font-bold text-slate-700 mb-1">Your name</label>
              <input className="input-base mb-4" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name" data-testid="auth-name" />
              <label className="block text-sm font-bold text-slate-700 mb-1">Mobile number</label>
              <input type="tel" className="input-base mb-4" required value={form.mobile_number}
                onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                placeholder="+44 7…" data-testid="auth-mobile" />
            </>
          )}
          <label className="block text-sm font-bold text-slate-700 mb-1">Email</label>
          <input type="email" required className="input-base mb-4" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@email.com" data-testid="auth-email" />
          <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
          <PasswordInput required minLength={6} className="input-base" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="At least 6 characters" data-testid="auth-password"
            toggleTestId="auth-password-toggle" />
          {mode === "register" && (
            <PasswordStrength password={form.password} testId="register-password-strength" />
          )}
          {mode === "login" && (
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
              <Link
                to="/auth/forgot"
                className="text-sm font-semibold text-brandPink hover:text-brandPinkDark"
                data-testid="auth-forgot-password-link"
              >
                Forgot password / PIN?
              </Link>
              <button
                type="button"
                onClick={() => setForgotEmailOpen(true)}
                className="text-sm font-semibold text-brandTeal hover:text-brandTealDark inline-flex items-center gap-1"
                data-testid="auth-forgot-email-btn"
              >
                <HelpCircle className="w-3.5 h-3.5" /> Forgot your email?
              </button>
            </div>
          )}

          {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="auth-error">{err}</div>}

          <button type="submit" disabled={busy} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="text-sm text-slate-500 mt-4 text-center">
            <Link to="/" className="hover:text-blue-600">← Back to home</Link>
          </div>
        </form>
      </div>

      {/* Forgot email modal — routes to support@mail.mytabletopapp.co.uk */}
      <Dialog open={forgotEmailOpen} onOpenChange={setForgotEmailOpen}>
        <DialogContent
          className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop"
          data-testid="forgot-email-modal"
        >
          <div className="relative bg-gradient-to-br from-brandTeal to-brandTealDark px-6 pt-8 pb-6 text-white">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="absolute -left-4 -bottom-8 w-24 h-24 rounded-full bg-white/10" />
            <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
              <HelpCircle className="w-4 h-4" /> Account recovery
            </div>
            <DialogHeader className="relative mt-2 text-left space-y-1">
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">
                Forgot your email?
              </DialogTitle>
              <DialogDescription className="text-white/90 text-sm leading-relaxed">
                No worries — we can look up your parent account for you.
                Drop our support team a note and we&rsquo;ll get you back in fast.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-6 bg-white">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandTeal mb-2">Email support</div>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Account%20recovery%20-%20forgot%20email`}
              className="btn-teal w-full justify-center break-all"
              data-testid="forgot-email-mailto"
              onClick={() => setForgotEmailOpen(false)}
            >
              <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
            </a>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Please include the child&rsquo;s first name and the approximate date you created the account so we can identify you safely.
              We reply within 24 hours.
            </p>
            <button
              type="button"
              onClick={() => setForgotEmailOpen(false)}
              className="w-full mt-4 text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1"
              data-testid="forgot-email-close"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
